import { memo, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { useNavSize } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useNavigate, type AnyRoute } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { NakedButton, WeaveLogoIcon } from '@frontend/design-system';
import { ICON_SIZE } from '../../../../constants';
import { AnimatedLogo } from '../../../top-bar-logo';
import WeaveLogoDark from '../../../Weave-Logo-Dark.svg';
import { TopBarFocusIds } from '../../utils/focus-registry';

type LogoButtonProps = {
  onClick: () => void;
  markAnimationDone: () => void;
  showVertical: boolean;
  animationDone: boolean;
  showCompact: boolean;
  isXsmall: boolean;
  vertical?: Vertical;
};

export const LogoButton = memo(
  ({ onClick, showVertical, animationDone, vertical, markAnimationDone, showCompact, isXsmall }: LogoButtonProps) => (
    <NakedButton
      css={css`
        align-items: center;
        color: ${theme.colors.white};
        display: flex;
        font-size: ${theme.fontSize(24)};
        padding: ${theme.spacing(0, 1)};
      `}
      onClick={onClick}
      id={TopBarFocusIds.logo}
    >
      {showVertical && (
        <>
          {animationDone ? (
            <img src={WeaveLogoDark} alt='weave-app-logo' css={{ height: ICON_SIZE }} data-tour-guide='portal-logo' />
          ) : (
            <AnimatedLogo vertical={vertical} onComplete={markAnimationDone} />
          )}
        </>
      )}
      {showCompact && !isXsmall && <WeaveLogoIcon color='default' size={ICON_SIZE} data-tour-guide='portal-logo' />}
    </NakedButton>
  )
);
LogoButton.displayName = 'LogoButton';

export const LogoButtonWrapper = () => {
  const navSize = useNavSize();
  const isXsmall = navSize.isLte('xsmall');
  const navigate = useNavigate();
  const { selectedLocationIds, getLocationName, accessibleLocationData } = useAppScopeStore();

  const singleLocationReferenceName = getLocationName(selectedLocationIds[0]);

  const verticalAnimationRef = useRef({ hasShown: false });
  useEffect(() => {
    verticalAnimationRef.current.hasShown = false;
  }, [singleLocationReferenceName]);

  return (
    (navSize.isGt('mobile') || !isXsmall) && (
      <LogoButton
        onClick={() => navigate({ to: '/home/dashboard' as AnyRoute })}
        showVertical={navSize.isGte('mobile')}
        showCompact={navSize.isLt('mobile')}
        isXsmall={isXsmall}
        animationDone={verticalAnimationRef.current.hasShown}
        vertical={accessibleLocationData?.[selectedLocationIds?.[0]]?.vertical}
        markAnimationDone={() => {
          verticalAnimationRef.current.hasShown = true;
        }}
      />
    )
  );
};
